import { defineComponent as _defineComponent } from 'vue'
import {ref, onMounted, computed, Ref} from 'vue';
import {useTranslations} from "@/hooks/useTranslations";
import VModal from "@/components/vModal.vue";
import ListRequest, {Filter, Ordering, OrderType} from "@/models/ListRequest";
import VModifyAdditionalCost from "@/components/costing/vModifyAdditionalCost.vue";
import VTable from "@/components/vTable.vue";
import {AdditionalCost} from "@/models/AdditionalCost/AdditionalCost";
import AdditionalCostRepository from "@/utils/API/AdditionalCostRepository";
import {useStoreState} from "@/hooks/useStoreState";
import {MeasurementCategory} from "@/models/Measurement/MeasurementCategory";
import MeasurementRepository from "@/utils/API/MeasurementRepository";
import {ListResult} from "@/utils/API/ResultTypes/ListResult";
import {Measurement} from "@/models/Measurement/Measurement";
import {AxiosResponse} from "axios";
import {IngredientCostMeasurement} from "@/models/IngredientCost/IngredientCostMeasurement";


export default /*#__PURE__*/_defineComponent({
  __name: 'vListAdditionalCost',
  props: {
  showAdditionalCostModal: {
    type: Boolean,
    required: true,
  },
  additionalCostIdsInInvoice: {
    type: Array<number>,
    required: true,
  },
},
  emits: ['on-cancel', 'on-add', 'on-remove', 'openAddVendorModal'],
  setup(__props, { emit }) {

const props = __props;



const translations = useTranslations();
const storeState = useStoreState();
let showModifyAdditionalCostModal = ref(false);

const localAdditionalCosts = ref<AdditionalCost[]>([]);

const totalCount = ref(0);
const count = ref(0);
const searchTerm = ref("");
const loading = ref(false);
const selectedAdditionalCost: Ref<AdditionalCost> = ref(null);
const filter = ref<Filter[]>([
  { name: "", value: "" },
]);
const massMeasurements = ref<IngredientCostMeasurement[]>([]);
const ordering = ref<Ordering>({
  field: "",
  orderType: OrderType.Asc,
});
const tableHeaders = computed(() => [
  { name: translate("cost_additional_table_name"), key: "Name", searchable: false, sortable: true },
  { name: translate("ingr-cost-table-vendor"), key: "Supplier", searchable: false, sortable: true },
  { name: translate("ingr-cost-table-quantity"), key: "Quantity", searchable: false, sortable: false },
  { name: translate("ingr-cost-table-measurement"), key: "Measurement", searchable: false, sortable: false },
  { name: translate("ingr-cost-table-cost"), key: "Cost", searchable: false, sortable: false },
  { name: translate("rec_invoice_add_new_cost"), key: "Add", searchable: false, searchString: "", sortable: true },
]);

const filteredAdditionalCosts = computed(() => {
  const filtered = localAdditionalCosts.value.filter((x) =>
    x.name.toLowerCase().includes(searchTerm.value.toLowerCase())
  );

  // Sort so that additionalCostIdsInInvoice come first
  return filtered.sort((a, b) => {
    const aInInvoice = props.additionalCostIdsInInvoice.includes(a.id);
    const bInInvoice = props.additionalCostIdsInInvoice.includes(b.id);

    if (aInInvoice === bInInvoice) return 0;
    return aInInvoice ? -1 : 1;
  });
});

const additionalCostsForGrid = computed(() =>
  filteredAdditionalCosts.value.map((x, index) => {
    const isSelected = props.additionalCostIdsInInvoice.includes(x.id);

    return {
      meta: {id: x.id, link: x.name},
      e1: x.vendor.name,
      e2: x.quantity,
      e3: x.ingredientCostMeasurement.measurement.name,
      e4: (Math.round(x.cost * 100) / 100).toFixed(2),
      e5: {
        component: "a",
        html: isSelected
          ? `<i class="fa fa-check-circle" style="font-size:30px; color: green""></i>`
          : `<i class="fa fa-plus-circle text-blue" style="font-size:30px;"></i>`,
        props: {
          href: "#",
          "data-target": "addIngredientCost",
          "data-toggle": "modal",
        },
        onClick: () =>
          isSelected
            ? removeIngredientCostFromInvoice(x.id)
            : addAdditionalCostToInvoice(x.id),
      },
    };
  })
);

const hasAdditionalCosts = computed(() => localAdditionalCosts.value.length > 0);

onMounted(async() => {
  await fetchAdditionalCosts();
  await loadMeasurements();
});

function addAdditionalCostToInvoice(id:number) {
  emit("on-add", id);
  closeModal();
}

function removeIngredientCostFromInvoice(id : number){
  emit("on-remove", id);
  closeModal();
}

function translate(key: string) {
  return translations.value.get(key);
}

function closeModal() {
  emit("on-cancel");
}

function openAddAdditionalCostModal() {
  // @ts-expect-error type is close enough
  selectedAdditionalCost.value = {
    id: 0,
    name: "",
    cost: 0,
    restaurantId: 0,
    recipeInvoiceId: 0,
    vendor: {
      name: "",
      restaurantId: 0,
      createdOn: null,
      id: 0
    },
    quantity: 0,
    ingredientCostMeasurementId: 0,
    vendorId: 0,
    updatedOn: null,
    createdOn: null,
    ingredientCostMeasurement: {
      measurement: {
        conversionCoeficient: 0,
        isDefault: false,
        measurementCategory: MeasurementCategory.Mass,
        name: "",
        id: 0,
      },
      weightInGrams: 0,
      measurementId: 0,
      ingredientCostId: 0,
      createdOn: null,
      id: 0
    },
    ingredientCostMeasurements: massMeasurements.value,
  };
  showModifyAdditionalCostModal.value = true;
}

function hideEditAdditionalCostModal(){
  showModifyAdditionalCostModal.value = false;
}

function refreshAdditionalCosts() {
  fetchAdditionalCosts();
}

function editAdditionalCost(itemId: number) {
  selectedAdditionalCost.value = localAdditionalCosts.value.find((x) => x.id === itemId) || null;
  showModifyAdditionalCostModal.value = true;
}

async function removeAdditionalCost(ids: number[]) {
  loading.value = true;
  try {
    await AdditionalCostRepository.deleteAdditionalCosts(ids);
    localAdditionalCosts.value = localAdditionalCosts.value.filter(c => !ids.includes(c.id));
  }
  catch (error) {
    console.error("Failed to fetch additional costs", error);
  }
  finally {
    loading.value = false;
  }
}

async function fetchAdditionalCosts() {
  loading.value = true;
  try {
    const response = await AdditionalCostRepository.getAdditionalCosts(storeState.value.AccountModule.userDetails.restaurantId);
    localAdditionalCosts.value = response.data;
  }
  catch (error) {
    console.error("Failed to fetch additional costs", error);
  }
  finally {
    loading.value = false;
  }
}

async function loadMeasurements() {
  let massRes: AxiosResponse<ListResult<Measurement>>;
  try {
    massRes = await MeasurementRepository.listMeasurementsForCategory(
      MeasurementCategory.Mass
    );
  }

  catch (err) {
    console.error("Failed to load measurements", err);
  }

  const mass = massRes.data.result.map((x) => ({
    id: x.id,
    weightInGrams: x.conversionCoeficient,
    ingredientCostId: 0,
    createOn: null,
    measurementId: 0,
    measurement: x,
  }));

  const volRes = await MeasurementRepository.listMeasurementsForCategory(
    MeasurementCategory.Volume
  );

  const volume = volRes.data.result
    .filter((v) => !massRes.data.result.some((m) => m.id === v.id))
    .map((x) => ({
      id: x.id,
      weightInGrams: x.conversionCoeficient,
      createOn: null,
      ingredientCostId: 0,
      measurementId: 0,
      measurement: x,
    }));

  massMeasurements.value = mass.concat(volume);
}

return { __sfc: true,props, translations, storeState, showModifyAdditionalCostModal, localAdditionalCosts, emit, totalCount, count, searchTerm, loading, selectedAdditionalCost, filter, massMeasurements, ordering, tableHeaders, filteredAdditionalCosts, additionalCostsForGrid, hasAdditionalCosts, addAdditionalCostToInvoice, removeIngredientCostFromInvoice, translate, closeModal, openAddAdditionalCostModal, hideEditAdditionalCostModal, refreshAdditionalCosts, editAdditionalCost, removeAdditionalCost, fetchAdditionalCosts, loadMeasurements, VModal, VModifyAdditionalCost, VTable }
}

})